import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Container,
  Stack,
  Button,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  CalendarMonth as CalendarMonthIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  StickyNote2Outlined as NoteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import UserProfileMenu from '../../components/UserProfileMenu';
import AuthorChip from '../../components/AuthorChip';
import { markdownToPlainText } from '../../utils/markdown';
import usePermissions from '../../hooks/usePermissions';
import { SiteScheduleService } from '../../services/SiteScheduleService';
import { useSiteTimelineData } from '../../hooks/useSiteTimelineData';
import {
  SiteSchedule,
  ScheduleEventType,
  ScheduleStatus,
  EVENT_TYPE_LABEL,
  SCHEDULE_STATUS_LABEL,
  MEMO_EVENT_TYPE,
  compareByScheduleTime,
  hasScheduleDate,
  scheduleTime,
} from '../../models/SiteSchedule';
import { SiteVisitLog } from '../../models/SiteVisitLog';
import { SiteIncident, SEVERITY_LABEL } from '../../models/SiteIncident';
import { isoOf } from './calendarUtils';
import MiniCalendar, { CalendarEvent } from '../ProjectDashboardPage/sections/MiniCalendar';
import ScheduleDetailDialog from '../ProjectDashboardPage/dialogs/ScheduleDetailDialog';
import VisitLogDetailDialog from '../ProjectDashboardPage/dialogs/VisitLogDetailDialog';
import ScheduleFormDialog from '../../components/dialogs/ScheduleFormDialog';
import RecordFormDialog from '../../components/dialogs/RecordFormDialog';
import QuickRecordFab from '../../components/QuickRecordFab';

const EVENT_COLOR: Record<ScheduleEventType, string> = {
  visit: '#1976d2',
  maintenance: '#0288d1',
  incident: '#d32f2f',
  meeting: '#7b1fa2',
  worklog: '#f57c00',
  // 메모는 청록 — 기존 6색(파랑/하늘/빨강/보라/주황/회색) 어디와도 겹치지 않게.
  memo: '#00897b',
  other: '#616161',
};

const STATUS_COLOR: Record<ScheduleStatus, 'default' | 'info' | 'success' | 'warning'> = {
  planned: 'info',
  in_progress: 'warning',
  done: 'success',
  cancelled: 'default',
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};

interface Snack {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

/**
 * 캘린더 범례용 색상 dot + 라벨.
 * `color` 는 hex 문자열(EVENT_COLOR.*) 또는 MUI palette key(예: "success.main") 모두 허용.
 * MUI sx 가 palette key 를 알아서 해석한다.
 */
const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const SiteSchedulesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { canEdit } = usePermissions();
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? Number(projectId) : NaN;

  const stored = localStorage.getItem('selectedProject');
  const projectName = stored ? (JSON.parse(stored).name as string | undefined) : undefined;

  // 일정/출장/장애 3종 조회는 공용 훅으로 통합 (UnifiedScheduleSection 과 공유).
  // visits→visitLogs, reload→refresh 로 alias 해 기존 참조를 그대로 유지.
  const {
    schedules,
    visits: visitLogs,
    incidents,
    loading,
    error,
    reload: refresh,
  } = useSiteTimelineData(projectIdNum);

  const [scheduleForm, setScheduleForm] = useState<{
    open: boolean;
    editing: SiteSchedule | null;
    presetStart: Date | null;
    /** 신규 등록 시 폼이 시작할 유형 — 'memo' 면 메모 모드로 열린다. */
    eventType: ScheduleEventType;
  }>({ open: false, editing: null, presetStart: null, eventType: 'visit' });

  // 등록(통합 다이얼로그)은 수정과 상태를 나눈다 — 종류 선택기 노출 여부가 다르고,
  // 저장된 항목을 다른 엔티티로 바꿀 수는 없다.
  const [createForm, setCreateForm] = useState<{
    open: boolean;
    presetStart: Date | null;
    presetEnd: Date | null;
  }>({
    open: false,
    presetStart: null,
    presetEnd: null,
  });

  // 선택된 일자 (YYYY-MM-DD). null 이면 다가오는 일정 + 최근 출장/장애 통합 뷰.
  const [selectedIso, setSelectedIso] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack>({ open: false, message: '', severity: 'success' });

  // 상세 보기 다이얼로그
  const [scheduleDetailId, setScheduleDetailId] = useState<number | null>(null);
  const [visitDetailId, setVisitDetailId] = useState<number | null>(null);

  const scheduleDetail = useMemo(
    () => (scheduleDetailId == null ? null : schedules.find((s) => s.id === scheduleDetailId) ?? null),
    [scheduleDetailId, schedules],
  );
  const visitDetail = useMemo(
    () => (visitDetailId == null ? null : visitLogs.find((v) => v.id === visitDetailId) ?? null),
    [visitDetailId, visitLogs],
  );

  // 일자별로 schedules/visits/incidents 묶기
  const byDay = useMemo(() => {
    const map = new Map<string, { schedules: SiteSchedule[]; visits: SiteVisitLog[]; incidents: SiteIncident[] }>();
    const ensure = (key: string) => {
      let v = map.get(key);
      if (!v) {
        v = { schedules: [], visits: [], incidents: [] };
        map.set(key, v);
      }
      return v;
    };
    // 시점 없는 메모는 일자 버킷에 넣을 수 없다 — isoOf(null) 은 epoch 를 거쳐
    // '1970-01-01' 이 되므로 걸러내지 않으면 그 날짜에 뭉쳐 붙는다.
    schedules
      .filter(hasScheduleDate)
      .forEach((s) => ensure(isoOf(s.scheduled_at as string)).schedules.push(s));
    visitLogs.forEach((v) => ensure(isoOf(v.visited_at)).visits.push(v));
    incidents.forEach((i) => ensure(isoOf(i.occurred_at)).incidents.push(i));
    return map;
  }, [schedules, visitLogs, incidents]);

  // 캘린더 마커: schedule = event_type 색, visit = info, incident = error.
  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const items: CalendarEvent[] = [];
    schedules.forEach((s) => {
      if (s.status === 'cancelled') return;
      if (!hasScheduleDate(s)) return; // 캘린더 마커는 날짜가 있어야 찍는다
      const color: CalendarEvent['color'] =
        s.event_type === 'incident'
          ? 'error'
          : s.event_type === 'maintenance'
            ? 'info'
            : s.event_type === 'meeting'
              ? 'secondary'
              : s.event_type === 'other'
                ? 'default'
                : 'primary';
      items.push({ iso: isoOf(s.scheduled_at as string), color });
    });
    visitLogs.forEach((v) => items.push({ iso: isoOf(v.visited_at), color: 'success' }));
    incidents.forEach((i) => items.push({ iso: isoOf(i.occurred_at), color: 'error' }));
    return items;
  }, [schedules, visitLogs, incidents]);

  // 다가오는 일정 (미선택 시 좌측 메인 콘텐츠).
  const upcomingSchedules = useMemo(() => {
    const now = Date.now();
    return [...schedules]
      .filter((s) => {
        const t = scheduleTime(s);
        return t !== null && t >= now;
      })
      .sort((a, b) => compareByScheduleTime(a, b, 'asc'));
  }, [schedules]);

  /**
   * 시점 없는 메모 — 캘린더/다가오는 일정 어디에도 못 들어가므로 별도 목록으로
   * 노출하지 않으면 등록해도 화면에서 사라진다. 최근 작성 순.
   */
  const memoSchedules = useMemo(
    () =>
      schedules
        .filter((s) => !hasScheduleDate(s))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [schedules],
  );

  /** 등록 — 종류(일정·메모·파일·출장 일지)는 통합 다이얼로그 안에서 고른다. */
  const openCreate = (preset?: Date, presetEnd?: Date) => {
    setCreateForm({ open: true, presetStart: preset ?? null, presetEnd: presetEnd ?? null });
  };

  const openEdit = (s: SiteSchedule) => {
    setScheduleForm({ open: true, editing: s, presetStart: null, eventType: s.event_type });
  };

  const handleDelete = async (s: SiteSchedule) => {
    if (!window.confirm(`'${s.title}' 일정을 삭제하시겠습니까?`)) return;
    try {
      await SiteScheduleService.delete(s.id);
      setSnack({ open: true, message: '삭제되었습니다', severity: 'success' });
      await refresh();
    } catch (e) {
      setSnack({ open: true, message: e instanceof Error ? e.message : '삭제 실패', severity: 'error' });
    }
  };

  const selectedDayData = selectedIso ? byDay.get(selectedIso) : undefined;

  const selectedDateLabel = useMemo(() => {
    if (!selectedIso) return null;
    const d = new Date(selectedIso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return selectedIso;
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
    });
  }, [selectedIso]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/project/${projectId}`)} size="small" aria-label="뒤로가기">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate(`/project/${projectId}`)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {projectName || `프로젝트 ${projectId}`}
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <CalendarMonthIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 스케줄
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <UserProfileMenu />
        </Box>
      </Paper>

      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          {/* Toolbar — 월 navigation 은 MiniCalendar 내부로 이동. 여기엔 제목/범례/액션만. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
          >
            <Typography variant="h6" fontWeight={700}>
              스케줄
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
              <LegendDot color={EVENT_COLOR.visit} label="출장" />
              <LegendDot color={EVENT_COLOR.maintenance} label="점검" />
              <LegendDot color={EVENT_COLOR.incident} label="장애" />
              <LegendDot color={EVENT_COLOR.meeting} label="미팅" />
              <LegendDot color="success.main" label="방문 기록" />
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="새로고침">
              <span>
                <IconButton onClick={refresh} disabled={loading} aria-label="새로고침">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            {/* 버튼이 곧 종류 — 업무 등록. 종류 선택 단계는 없앴다. */}
            {canEdit && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate()}>
                업무 등록
              </Button>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            /* 좌(메인 리스트) / 우(컴팩트 캘린더). md 미만은 캘린더가 위로 오는 stack. */
            <Box
              data-testid="schedules-layout"
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
                alignItems: 'start',
              }}
            >
              {/* 좌측: 선택 날짜 상세 또는 다가오는 일정 */}
              <Box sx={{ order: { xs: 2, md: 1 }, minWidth: 0 }} data-testid="schedules-main">
                {selectedIso ? (
                  <>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        {selectedDateLabel}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => setSelectedIso(null)}>
                          전체 보기
                        </Button>
                        {canEdit && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const d = new Date(selectedIso + 'T09:00:00');
                              openCreate(d);
                            }}
                          >
                            이 날 일정 추가
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                    <Stack spacing={2}>
                      {/* 계획 일정 */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          계획 일정 ({selectedDayData?.schedules.length ?? 0}건)
                        </Typography>
                        {selectedDayData?.schedules.length ? (
                          <Stack spacing={1}>
                            {selectedDayData.schedules.map((s) => (
                              <Paper
                                key={s.id}
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                                onClick={() => setScheduleDetailId(s.id)}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  spacing={1}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                                    >
                                      <Chip
                                        label={EVENT_TYPE_LABEL[s.event_type]}
                                        size="small"
                                        sx={{ bgcolor: EVENT_COLOR[s.event_type], color: 'white' }}
                                      />
                                      <Chip
                                        label={SCHEDULE_STATUS_LABEL[s.status]}
                                        size="small"
                                        color={STATUS_COLOR[s.status]}
                                        variant="outlined"
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        {formatTime(s.scheduled_at)}
                                        {s.scheduled_end_at ? `–${formatTime(s.scheduled_end_at)}` : ''}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="body2" fontWeight={600}>
                                      {s.title}
                                    </Typography>
                                    {s.description && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          mt: 0.5,
                                        }}
                                      >
                                        {markdownToPlainText(s.description)}
                                      </Typography>
                                    )}
                                    {(s.assignee || s.location) && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5 }}
                                      >
                                        {s.assignee && `담당: ${s.assignee}`}
                                        {s.assignee && s.location && ' · '}
                                        {s.location && `위치: ${s.location}`}
                                      </Typography>
                                    )}
                                  </Box>
                                  {canEdit && (
                                    <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                      <Tooltip title="수정">
                                        <IconButton size="small" onClick={() => openEdit(s)}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="삭제">
                                        <IconButton size="small" color="error" onClick={() => handleDelete(s)}>
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            등록된 일정 없음
                          </Typography>
                        )}
                      </Box>

                      {/* 출장 일지 (그 날짜) */}
                      {selectedDayData?.visits.length ? (
                        <>
                          <Divider />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                              출장 일지 ({selectedDayData.visits.length}건)
                            </Typography>
                            <Stack spacing={1}>
                              {selectedDayData.visits.map((v) => (
                                <Paper
                                  key={v.id}
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    bgcolor: 'background.default',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                  }}
                                  onClick={() => setVisitDetailId(v.id)}
                                >
                                  <Typography variant="body2" fontWeight={600}>
                                    {v.purpose}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {v.visitor_name} · {formatTime(v.visited_at)}
                                  </Typography>
                                </Paper>
                              ))}
                            </Stack>
                            <Button
                              size="small"
                              sx={{ mt: 0.5 }}
                              onClick={() => navigate(`/project/${projectId}/journal`)}
                            >
                              일지 전체 보기 →
                            </Button>
                          </Box>
                        </>
                      ) : null}

                      {/* 장애 이력 (그 날짜) */}
                      {selectedDayData?.incidents.length ? (
                        <>
                          <Divider />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              gutterBottom
                              color="error"
                            >
                              장애 이력 ({selectedDayData.incidents.length}건)
                            </Typography>
                            <Stack spacing={1}>
                              {selectedDayData.incidents.map((i) => (
                                <Paper key={i.id} variant="outlined" sx={{ p: 1.5 }}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    sx={{ rowGap: 0.5 }}
                                  >
                                    <Chip
                                      label={SEVERITY_LABEL[i.severity]}
                                      size="small"
                                      variant="outlined"
                                      color={
                                        i.severity === 'critical' || i.severity === 'high'
                                          ? 'error'
                                          : 'default'
                                      }
                                    />
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      sx={{ flex: 1, minWidth: 0 }}
                                    >
                                      {i.title}
                                    </Typography>
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                            <Button
                              size="small"
                              sx={{ mt: 0.5 }}
                              onClick={() => navigate(`/project/${projectId}/incidents`)}
                            >
                              장애 이력 전체 보기 →
                            </Button>
                          </Box>
                        </>
                      ) : null}
                    </Stack>
                  </>
                ) : (
                  <>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1.5 }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        다가오는 일정 ({upcomingSchedules.length}건)
                      </Typography>
                    </Stack>

                    <Paper variant="outlined">
                      {upcomingSchedules.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ p: 3, textAlign: 'center' }}
                        >
                          예정된 일정이 없습니다. 우측 캘린더의 칸을 클릭해 과거 일정을 확인하거나
                          "일정 추가" 로 새 일정을 등록하세요.
                        </Typography>
                      ) : (
                        <Stack divider={<Divider />}>
                          {upcomingSchedules.slice(0, 30).map((s) => (
                            <Box
                              key={s.id}
                              sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                              onClick={() => setScheduleDetailId(s.id)}
                            >
                              <Box sx={{ minWidth: 110 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(s.scheduled_at).toLocaleDateString('ko-KR', {
                                    month: 'short',
                                    day: 'numeric',
                                    weekday: 'short',
                                  })}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  {formatTime(s.scheduled_at)}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                                >
                                  <Chip
                                    label={EVENT_TYPE_LABEL[s.event_type]}
                                    size="small"
                                    sx={{
                                      bgcolor: EVENT_COLOR[s.event_type],
                                      color: 'white',
                                      height: 20,
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                  <Chip
                                    label={SCHEDULE_STATUS_LABEL[s.status]}
                                    size="small"
                                    color={STATUS_COLOR[s.status]}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                </Stack>
                                <Typography variant="body2" fontWeight={500}>
                                  {s.title}
                                </Typography>
                                {(s.assignee || s.location) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.25 }}
                                  >
                                    {[s.assignee, s.location].filter(Boolean).join(' · ')}
                                  </Typography>
                                )}
                                {(s.created_by_name || s.created_by_id) && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <AuthorChip name={s.created_by_name} userId={s.created_by_id} label="" dense />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Paper>

                    {/* 날짜 없는 메모 — 캘린더에도 '다가오는 일정' 에도 자리가
                        없으므로 여기서만 보인다. 없으면 섹션 자체를 숨긴다. */}
                    {memoSchedules.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1.5 }}
                        >
                          <NoteIcon fontSize="small" sx={{ color: EVENT_COLOR.memo }} />
                          <Typography variant="subtitle1" fontWeight={700}>
                            공지사항 ({memoSchedules.length}건)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            날짜 없이 남긴 내용
                          </Typography>
                          {/* 이 현장 것만 보인다 — 모든 현장 메모는 공통 메모 화면에서. */}
                          <Button
                            size="small"
                            sx={{ ml: 'auto' }}
                            onClick={() => navigate('/memos')}
                          >
                            공통 메모
                          </Button>
                        </Stack>
                        <Paper variant="outlined">
                          <Stack divider={<Divider />}>
                            {memoSchedules.slice(0, 30).map((s) => (
                              <Box
                                key={s.id}
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                  cursor: 'pointer',
                                  borderLeft: '3px solid',
                                  borderLeftColor: EVENT_COLOR.memo,
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                                onClick={() => setScheduleDetailId(s.id)}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {s.title}
                                </Typography>
                                {s.description && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      mt: 0.25,
                                    }}
                                  >
                                    {markdownToPlainText(s.description)}
                                  </Typography>
                                )}
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                                >
                                  {s.assignee && (
                                    <Typography variant="caption" color="text.secondary">
                                      {s.assignee}
                                    </Typography>
                                  )}
                                  {(s.created_by_name || s.created_by_id) && (
                                    <AuthorChip
                                      name={s.created_by_name}
                                      userId={s.created_by_id}
                                      label=""
                                      dense
                                    />
                                  )}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* 우측: 컴팩트 캘린더. sticky 로 스크롤해도 따라옴. */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  order: { xs: 1, md: 2 },
                  position: { md: 'sticky' },
                  top: { md: 16 },
                  minWidth: 0,
                }}
              >
                <MiniCalendar
                  events={calendarEvents}
                  selectedIso={selectedIso}
                  onSelect={setSelectedIso}
                  // 날짜를 이어 드래그 → 그 기간(첫날 09:00 ~ 마지막 날 18:00)으로
                  // 시작·종료가 채워진 등록 모달.
                  onRangeSelect={
                    canEdit
                      ? (s, e) =>
                          openCreate(new Date(`${s}T09:00:00`), new Date(`${e}T18:00:00`))
                      : undefined
                  }
                  size="sm"
                />
                <Divider sx={{ my: 1.25 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                  칸 클릭 → 그 날 상세. 재클릭 시 해제.
                  {canEdit ? ' 드래그로 기간 일정 등록.' : ''}
                </Typography>
              </Paper>
            </Box>
          )}
        </Paper>
      </Container>

      {/* 목록을 아래로 훑다가도 위로 올라가지 않고 등록할 수 있게 */}
      {canEdit && <QuickRecordFab onClick={() => openCreate()} label="업무 등록" />}

      {/* 업무 등록 전용 — kinds 를 하나로 좁혀 종류 선택기를 숨긴다. */}
      <RecordFormDialog
        open={createForm.open}
        kinds={['schedule']}
        projectId={projectIdNum}
        siteName={projectName}
        presetStart={createForm.presetStart}
        presetEnd={createForm.presetEnd}
        onClose={() => setCreateForm({ open: false, presetStart: null, presetEnd: null })}
        onSaved={(r) => {
          setCreateForm({ open: false, presetStart: null, presetEnd: null });
          setSnack({
            open: true,
            // 조사가 달라서(일정'이' / 메모'가') 문장을 통째로 고른다.
            message:
              r.kind === 'file'
                ? `파일 ${r.uploadedCount ?? 0}건이 올라갔습니다`
                : r.kind === 'memo'
                  ? '메모가 등록되었습니다'
                  : r.kind === 'visitlog'
                    ? '출장 일지가 등록되었습니다'
                    : '일정이 등록되었습니다',
            severity: 'success',
          });
          void refresh();
        }}
      />

      {/* 수정 — 일정↔메모는 같은 테이블이라 이 폼 안에서 서로 전환된다. */}
      <ScheduleFormDialog
        open={scheduleForm.open}
        projectId={projectIdNum}
        editing={scheduleForm.editing}
        presetStart={scheduleForm.presetStart}
        initialEventType={scheduleForm.eventType}
        onClose={() => setScheduleForm((s) => ({ ...s, open: false }))}
        onSaved={(saved) => {
          setScheduleForm((s) => ({ ...s, open: false }));
          setSnack({
            open: true,
            message:
              saved.event_type === MEMO_EVENT_TYPE ? '메모가 수정되었습니다' : '일정이 수정되었습니다',
            severity: 'success',
          });
          void refresh();
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <ScheduleDetailDialog
        open={scheduleDetailId != null}
        schedule={scheduleDetail}
        onClose={() => setScheduleDetailId(null)}
        onEdit={(s) => {
          setScheduleDetailId(null);
          openEdit(s);
        }}
        onDelete={async (s) => {
          await handleDelete(s);
          setScheduleDetailId(null);
        }}
      />

      <VisitLogDetailDialog
        open={visitDetailId != null}
        log={visitDetail}
        onClose={() => setVisitDetailId(null)}
      />
    </Box>
  );
};

export default SiteSchedulesPage;
